/**
 * Cursor-follow grid glow — pointer position + zoom-scaled mask/fade for the
 * glow layer. Grid alignment comes from a duplicate `<Background />` inside
 * the glow layer (same React Flow pattern math as the base grid, lines or dots).
 */

import type { Viewport } from "@xyflow/react";
import { useCallback, useEffect, type RefObject } from "react";

const BASE_GLOW_RADIUS_PX = 160;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function applyGlowZoom(el: HTMLElement, zoom: number) {
  el.style.setProperty(
    "--canvas-glow-r",
    `${BASE_GLOW_RADIUS_PX * Math.min(1, zoom / 0.5)}px`,
  );
  el.style.setProperty(
    "--canvas-grid-fade",
    String(clamp((zoom - 0.3) / 0.2, 0, 1)),
  );
}

export function useCanvasCursorGlow(
  wrapperRef: RefObject<HTMLDivElement | null>,
  options: { enabled: boolean },
) {
  const { enabled } = options;

  const syncViewport = useCallback(
    (viewport: Viewport) => {
      const el = wrapperRef.current;
      if (!el || !enabled) return;
      applyGlowZoom(el, viewport.zoom);
    },
    [wrapperRef, enabled],
  );

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || !enabled) return;

    // Coalesce pointermove into a single rAF flush — high-poll mice fire many
    // events per frame; we only need the latest position once per frame. The
    // rect is read inside the flush (not cached, not per event): at most one
    // getBoundingClientRect per frame, and always fresh — so the glow stays put
    // even when the wrapper's screen position shifts from sibling layout (e.g.
    // a banner/chat panel above it) without a resize or scroll event.
    let rafId = 0;
    let pendingClientX = 0;
    let pendingClientY = 0;
    let hasPending = false;

    const flush = () => {
      rafId = 0;
      if (!hasPending) return;
      hasPending = false;
      const rect = el.getBoundingClientRect();
      // Unrounded floats — drives mask-position (and, in Stage 2, a pair of
      // exactly-cancelling transforms), so rounding here would misalign.
      el.style.setProperty(
        "--canvas-glow-tx",
        `${pendingClientX - rect.left}px`,
      );
      el.style.setProperty(
        "--canvas-glow-ty",
        `${pendingClientY - rect.top}px`,
      );
    };

    const onPointerMove = (e: PointerEvent) => {
      pendingClientX = e.clientX;
      pendingClientY = e.clientY;
      hasPending = true;
      if (rafId === 0) rafId = requestAnimationFrame(flush);
    };
    const onPointerLeave = () => {
      if (rafId !== 0) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      hasPending = false;
      // Park off-screen so the masked circle is fully clipped away.
      el.style.setProperty("--canvas-glow-tx", "-9999px");
      el.style.setProperty("--canvas-glow-ty", "-9999px");
    };

    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerleave", onPointerLeave);
    return () => {
      if (rafId !== 0) cancelAnimationFrame(rafId);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerleave", onPointerLeave);
    };
  }, [wrapperRef, enabled]);

  return { syncViewport };
}
